import type { TableColumn } from '@/types/tableTypes';
import { isModelDataType } from '@/types/columnTypes';
import { idLookupDisplayString } from '@/constants/lookupFilterOptions';
import { formatUsMmDdYy, formatUsMmDdYyTime12h } from '@/utils/date';
import { booleanDisplayString, booleanDisplayStringOrNull } from '@/utils/booleanDisplay';
import { isPasswordColumn, PASSWORD_MASK } from '@/components/record/fields/fieldKind';

/**
 * Plain-text value formatting for the compact master-list rows in the search
 * page's cards view. Intentionally NOT ColumnMapping: a page of rows × dozens
 * of visible columns must stay cheap, and the summary is one line-clamped
 * string anyway.
 */
/** A calendar date with no time part, such as a schedule's '2026-11-08'. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function formatDateSummary(value: string): string {
    // A plain date has no time to show. Printing it with the clock would read "12:00 AM".
    return DATE_ONLY_PATTERN.test(value) ? formatUsMmDdYy(value) : formatUsMmDdYyTime12h(value);
}

export function formatSummaryValue(value: unknown, dataType?: string): string | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    // Boolean columns first: Elasticsearch hands some of them back as 0/1, which
    // the `typeof value === 'number'` branch below would print raw. The table
    // cell (DefaultColumn) resolves that shape to Yes/No, so without this the
    // same field on the same row reads "Yes" in the table and "1" in cards view.
    // Keyed off the declared dataType, so a genuine number holding 0 or 1 is
    // untouched.
    if (dataType === 'boolean') {
        return booleanDisplayStringOrNull(value);
    }
    if (typeof value === 'string') {
        return dataType === 'Date' ? formatDateSummary(value) : value;
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (typeof value === 'boolean') {
        return booleanDisplayString(value);
    }
    if (Array.isArray(value)) {
        return value.length > 0 ? String(value.length) : null;
    }
    if (typeof value === 'object') {
        const display = (value as { display_string?: unknown }).display_string;
        return typeof display === 'string' && display.trim() !== '' ? display : null;
    }
    return null;
}

/**
 * Long unbreakable runs (playlist names, hashes, file names) have no soft-wrap
 * opportunities, so the row's line-clamp ellipsis lands at the previous word
 * boundary and the rest of the line is left empty. Injecting zero-width spaces
 * lets such runs wrap at the container edge instead; short natural-language
 * words are untouched.
 */
const UNBREAKABLE_RUN_PATTERN = /\S{20,}/g;

function makeLongRunsSoftWrappable(text: string): string {
    return text.replace(UNBREAKABLE_RUN_PATTERN, (unbreakableRun) =>
        unbreakableRun.split('').join('\u200B')
    );
}

export interface SummaryPart {
    /** Column id — stable v-for key. */
    key: string;
    header: string;
    text: string;
    /**
     * Present when the value is a singular foreign-model relation with a numeric
     * id: the cards view renders these as click-to-inspect links opening the
     * related record in the side-peek inspector.
     */
    relation?: { entityId: string; recordId: number };
    /**
     * Present when the value is a login password. `text` is then eight dots,
     * and the card draws a password field that can show this.
     */
    password?: string;
}

/** Structured "Header value" parts of the row's visible columns, in column order. */
export function buildRowSummaryParts(
    row: Record<string, unknown>,
    visibleColumns: TableColumn[],
    excludeFields: string[] = []
): SummaryPart[] {
    const parts: SummaryPart[] = [];
    for (const column of visibleColumns) {
        // Custom table-only columns (e.g. the Play button) have no model value.
        if (excludeFields.includes(column.id) || column.dataType === 'display') {
            continue;
        }
        const value = row[column.id];
        if (isPasswordColumn(column)) {
            if (value !== null && value !== undefined && value !== '') {
                parts.push({ key: column.id, header: column.header, text: PASSWORD_MASK, password: String(value) });
            }
            continue;
        }
        // Integer FK columns backed by a bundled id lookup (e.g. brand_id) print
        // the option's display string, not the raw id ("Brand TPG", not "Brand 2").
        const formatted =
            idLookupDisplayString(column.filterControl?.optionsSource, value) ??
            formatSummaryValue(value, column.dataType);
        if (formatted === null) {
            continue;
        }
        const part: SummaryPart = {
            key: column.id,
            header: column.header,
            text: makeLongRunsSoftWrappable(formatted),
        };
        const relationId =
            value !== null && typeof value === 'object' && !Array.isArray(value)
                ? (value as { id?: unknown }).id
                : undefined;
        if (typeof relationId === 'number' && isModelDataType(column.dataType ?? '')) {
            part.relation = { entityId: column.dataType!, recordId: relationId };
        }
        parts.push(part);
    }
    return parts;
}

/** "Header value · Header value · …" summary of the row's visible columns. */
export function buildRowSummary(
    row: Record<string, unknown>,
    visibleColumns: TableColumn[],
    excludeFields: string[] = []
): string {
    return buildRowSummaryParts(row, visibleColumns, excludeFields)
        .map((part) => `${part.header} ${part.text}`)
        .join(' · ');
}
